import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Droplets, Filter, RefreshCw, Timer, Database, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img1 from "../Images/watertreatment.png";
import img2 from "../Images/DMplant.jpg";
import img3 from "../Images/filtersoftner.jpg";
import img4 from "../Images/reverseosmosis.jpg";
import img5 from "../Images/ultraflitration.jpg"
import img6 from "../Images/multimedia.jpg";
import img7 from "../Images/effulentplant.jpg";
import img8 from "../Images/sewagetreat.webp";

const WaterManagement = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const sections = {
    demineralisation: {
      title: "Demineralisation Plant",
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Demineralisation is both a generic term for processes for removal of dissolved solids from water, and a specific term for certain ion exchange processes for that same purpose.",
      features: [
        "Complete removal of dissolved solids",
        "Ion exchange process with multiple steps",
        "Capacity range from 0.5 to 350 m³/h per line",
        "High-quality treated water output",
        "Automated regeneration system",
        "Advanced monitoring and control"
      ],
      applications: [
        "Power plants",
        "Pharmaceutical industries",
        "Electronics manufacturing",
        "Chemical processing",
        "Food and beverage production"
      ],
      image: img2
    },
    filter: {
      title: "Filter & Softener Plant",
      icon: <Filter className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Comprehensive water treatment solution combining filtration for removing impurities and softening for reducing water hardness through ion exchange process.",
      features: [
        "Dual-stage treatment process",
        "Automatic regeneration cycle",
        "Advanced control system",
        "Low maintenance requirements",
        "Continuous monitoring",
        "Efficient hardness removal"
      ],
      applications: [
        "Commercial buildings",
        "Industrial processes",
        "Hotels and restaurants",
        "Residential complexes",
        "Manufacturing facilities"
      ],
      image: img3
    },
    ro: {
      title: "Reverse Osmosis Plant (R.O)",
      icon: <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "State-of-the-art reverse osmosis system for efficient removal of dissolved salts and impurities from water through membrane filtration.",
      features: [
        "High recovery rates",
        "Energy-efficient design",
        "Advanced membrane technology",
        "Automated cleaning system",
        "Real-time monitoring",
        "Modular construction"
      ],
      applications: [
        "Desalination plants",
        "Industrial processes",
        "Pharmaceutical manufacturing",
        "Food and beverage industry",
        "Healthcare facilities"
      ],
      image: img4
    },
    uf: {
      title: "Ultra Filtration Plant (U.F)",
      icon: <Timer className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced ultrafiltration system for removal of bacteria, viruses, and high molecular weight compounds using low-pressure membrane technology.",
      features: [
        "Improved product quality",
        "Low operating pressure",
        "High permeability",
        "Removal of microbiological matter",
        "Colloidal matter removal",
        "Consistent water quality"
      ],
      applications: [
        "RO pretreatment",
        "Potable water treatment",
        "Industrial water filtration",
        "Membrane Bio-Reactor",
        "Wastewater treatment"
      ],
      image: img5
    },
    media: {
      title: "Media Filtration",
      icon: <Droplets className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Specialized filtration systems using various media types for removal of specific contaminants and impurities from water.",
      features: [
        "Multiple media options",
        "Custom design solutions",
        "High filtration efficiency",
        "Backwash capability",
        "Long service life",
        "Low maintenance"
      ],
      applications: [
        "Municipal water treatment",
        "Industrial processes",
        "Swimming pools",
        "Irrigation systems",
        "Groundwater treatment"
      ],
      image: img6
    }
  };

  const additionalSystems = {
    effluent: {
      title: "Effluent Treatment Plant",
      description: "Comprehensive wastewater treatment system designed to meet specific discharge requirements and environmental regulations.",
      components: [
        "Clarifier systems",
        "Chemical feed units",
        "Advanced filtration",
        "pH adjustment systems",
        "Automated control panels",
        "Biological treatment units"
      ],
      image: img7
    },
    sewage: {
      title: "Sewage Treatment Plant",
      description: "State-of-the-art sewage treatment facilities for processing and treating municipal and industrial wastewater.",
      components: [
        "Primary treatment units",
        "Secondary biological treatment",
        "Tertiary treatment systems",
        "Sludge handling equipment",
        "Disinfection systems",
        "Monitoring and control systems"
      ],
      image: img8
    }
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Water Treatment Solutions & Systems",
    "description": "Comprehensive water treatment plants including demineralisation, RO, UF, filtration, effluent and sewage treatment systems",
    "itemListElement": [
      ...Object.entries(sections).map(([key, section], index) => ({
        "@type": "Product",
        "position": index + 1,
        "name": section.title,
        "description": section.description,
        "image": section.image
      })),
      ...Object.entries(additionalSystems).map(([key, system], index) => ({
        "@type": "Product",
        "position": Object.keys(sections).length + index + 1,
        "name": system.title,
        "description": system.description,
        "image": system.image
      }))
    ]
  };

  return (
    <>
      <Helmet>
        <title>Water Treatment Solutions | RO, DM, UF Plants & Wastewater Treatment | Gortherm Energy</title>
        <meta name="description" content="Comprehensive water treatment solutions including demineralisation plants, reverse osmosis systems, ultrafiltration, water softeners, effluent treatment, and sewage treatment plants. Advanced purification technology for industrial and municipal applications." />
        <meta name="keywords" content="water treatment plant, demineralisation plant, reverse osmosis, RO plant, ultrafiltration, water softener, effluent treatment plant, sewage treatment plant, wastewater treatment, industrial water purification, water filtration systems Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Water Treatment Solutions | RO, DM, UF & Wastewater Treatment | Gortherm Energy" />
        <meta property="og:description" content="Cutting-edge water treatment technologies for sustainable water management. Complete treatment systems from purification to wastewater management." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/water-treatment" />
        <meta property="og:image" content={img1} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Water Treatment Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Advanced water treatment plants and wastewater management systems for industrial and municipal applications." />
        <meta name="twitter:image" content={img1} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/water-treatment" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden"
        >
          <img
            src={img1}
            alt="Advanced water treatment solutions and purification systems by Gortherm Energy"
            className="w-full h-full object-cover transform scale-105"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Water Treatment
                  <span className="text-sky-400"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8">
                  Cutting-edge water treatment technologies for a sustainable future
                </p>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Main Treatment Systems */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
            Water Treatment Systems
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          >
            {Object.entries(sections).map(([key, section], idx) => (
              <motion.article
                key={key}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredCard(idx)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="relative h-40 sm:h-48">
                  <img
                    src={section.image}
                    alt={`${section.title} - Advanced water purification system`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                    itemProp="image"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-blue-600" aria-hidden="true">
                      {section.icon}
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900" itemProp="name">
                      {section.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-4" itemProp="description">
                    {section.description}
                  </p>
                  <button
                    onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base font-medium"
                    aria-expanded={expandedSection === idx}
                    aria-controls={`treatment-details-${idx}`}
                  >
                    <span>Learn more</span>
                    <motion.div
                      animate={{ rotate: expandedSection === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === idx && (
                      <motion.div
                        id={`treatment-details-${idx}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 space-y-4"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Key Features</h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {section.features.map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Applications</h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {section.applications.map((app, i) => (
                              <li key={i}>{app}</li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* Additional Treatment Systems */}
        <section className="bg-gray-100 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
              Specialized Treatment Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {Object.entries(additionalSystems).map(([key, system]) => (
                <motion.article
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-xl"
                  itemScope
                  itemType="https://schema.org/Product"
                >
                  <div className="relative h-48 sm:h-64">
                    <img
                      src={system.image}
                      alt={`${system.title} - Industrial wastewater treatment facility`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="600"
                      height="400"
                      itemProp="image"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4" itemProp="name">
                      {system.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-6" itemProp="description">
                      {system.description}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Key Components</h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {system.components.map((component, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-xs sm:text-sm text-gray-700">
                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <span>{component}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
              Why Choose Our Water Treatment Solutions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Advanced Technology</h3>
                <p className="text-sm sm:text-base">
                  State-of-the-art treatment systems incorporating latest membrane technology, 
                  automation, and monitoring for optimal water quality and efficiency.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Environmental Compliance</h3>
                <p className="text-sm sm:text-base">
                  All systems designed to meet stringent environmental regulations and discharge 
                  standards, ensuring responsible water management.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Custom Solutions</h3>
                <p className="text-sm sm:text-base">
                  Tailored water treatment designs based on specific water quality requirements, 
                  flow rates, and application needs.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-600">Complete Support</h3>
                <p className="text-sm sm:text-base">
                  Comprehensive services from design and installation to commissioning, operation 
                  training, and ongoing maintenance support.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Optimize Your Water Treatment?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our water treatment experts are ready to help you design and implement 
              the perfect purification solution for your facility.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact water treatment experts at +977 980 278 1400"
            >
              <button className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            <p className="mt-4 text-sm sm:text-base text-gray-600">
              Call us at: <a href="tel:+9779802781400" className="text-blue-600 hover:text-blue-700 font-semibold">+977 980 278 1400</a>
            </p>
          </div>
        </aside>
      </main>
    </>
  );
};

export default WaterManagement;